
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Button, Chip, Container, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, IconButton, Paper, TextField,
  Typography, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Switch, FormControlLabel, Tabs, Tab,
  Alert, Snackbar, Tooltip, Card, CardContent, useMediaQuery, useTheme,MenuItem
} from "@mui/material";
import { Add, Edit, Delete, Close, CheckCircle, Cancel } from "@mui/icons-material";
import { apiRequest } from "../../fetchSetup";
import axios from 'axios';

const SIDEBAR_WIDTH = 280;

//  COLOR PRESETS
const COLOR_PRESETS = [
  { label: "Teal",   badgeColor: "#84CC16", accentColor: "#80ED99", bg: "linear-gradient(145deg, #006D77 0%, #004A52 100%)", badgeBg: "#E0F7F8", textColor: "#006D77" },
  { label: "Gold",   badgeColor: "#F59E0B", accentColor: "#FDE68A", bg: "linear-gradient(145deg, #92400E 0%, #78350F 100%)", badgeBg: "#FEF3C7", textColor: "#D97706" },
  { label: "Blue",   badgeColor: "#0284C7", accentColor: "#38BDF8", bg: "linear-gradient(145deg, #075985 0%, #0C4A6E 100%)", badgeBg: "#E0F2FE", textColor: "#0284C7" },
  { label: "Purple", badgeColor: "#7C3AED", accentColor: "#A78BFA", bg: "linear-gradient(145deg, #4C1D95 0%, #3B0764 100%)", badgeBg: "#EDE9FE", textColor: "#7C3AED" },
  { label: "Rose",   badgeColor: "#DB2777", accentColor: "#F9A8D4", bg: "linear-gradient(145deg, #9D174D 0%, #831843 100%)", badgeBg: "#FCE7F3", textColor: "#DB2777" },
  { label: "Amber",  badgeColor: "#D97706",accentColor: "#FCD34D",bg: "linear-gradient(145deg, #B45309 0%, #92400E 100%)",badgeBg: "#FEF3C7",textColor: "#B45309"},
  { label: "Teal",   badgeColor: "#0D9488",accentColor: "#5EEAD4",bg: "linear-gradient(145deg, #0D9488 0%, #087168 100%)",badgeBg: "#CCFBF1",textColor: "#0D9488"},

];

const getPreset = (idx) => COLOR_PRESETS[(idx ?? 0) % COLOR_PRESETS.length];
const API = process.env.REACT_APP_HEALTH_CAMP_ADMIN_API;

// ── Default form shapes ───────────────────────────────────────────────────────
const emptycamp = {
  id: "", icon: "🏥", badge: "FREE", title: "", subtitle: "",
  dates: "", tests: "", note: "", tagline: "",
  phone: "+91 7661889977", website: "",
  is_active: false, sort_order: 99, preset_index: 0,community: "",
};

const emptypkg = {
  id: "", name: "", icon: "💊", tier: "Standard",
  tagline: "", total: "", offerPrice: "", tests: "",
  is_active: false, sort_order: 99, preset_index: 0,test_count:0
};

// ── Status chip ───────────────────────────────────────────────────────────────
const StatusChip = ({ active }) =>
  active ? (
    <Chip label="Active" size="small" icon={<CheckCircle sx={{ fontSize: 14 }} />}
      sx={{ bgcolor: "#D1FAE5", color: "#065F46", fontWeight: 700, fontSize: { xs: 11, sm: 12 } }} />
  ) : (
    <Chip label="Inactive" size="small" icon={<Cancel sx={{ fontSize: 14 }} />}
      sx={{ bgcolor: "#FEE2E2", color: "#991B1B", fontWeight: 700, fontSize: { xs: 11, sm: 12 } }} />
  );

// ── Colour preset picker ──────────────────────────────────────────────────────
const PresetPicker = ({ value, onChange }) => (
  <Box>
    <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: "#64748B", mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
      Colour Theme
    </Typography>
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
      {COLOR_PRESETS.map((p, i) => (
        <Tooltip key={i} title={p.label}>
          <Box onClick={() => onChange(i)} sx={{
            width: { xs: 32, sm: 38 }, height: { xs: 32, sm: 38 }, borderRadius: 1.5, cursor: "pointer",
            background: p.bg,
            border: value === i ? "3px solid #0F172A" : "3px solid transparent",
            boxShadow: value === i ? "0 0 0 2px #fff, 0 0 0 4px #0F172A" : "0 2px 6px rgba(0,0,0,0.18)",
            transition: "all 0.15s",
            display: "flex", alignItems: "center", justifyContent: "center",
            "&:hover": { transform: "scale(1.14)" },
          }}>
            {value === i && <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "#fff", opacity: 0.9 }} />}
          </Box>
        </Tooltip>
      ))}
      {value !== undefined && (
        <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: "#94A3B8", ml: 0.5 }}>
          {COLOR_PRESETS[value]?.label} theme selected
        </Typography>
      )}
    </Box>
  </Box>
);

// ── Colour swatch ─────────────────────────────────────────────────────────────
const ColorSwatch = ({ presetIndex }) => {
  const p = getPreset(presetIndex);
  return (
    <Tooltip title={`${p.label} theme`}>
      <Box sx={{
        width: { xs: 20, sm: 24 }, height: { xs: 20, sm: 24 }, borderRadius: 1, background: p.bg, flexShrink: 0,
        border: "1.5px solid rgba(0,0,0,0.1)", boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
      }} />
    </Tooltip>
  );
};

// ── Dialog header ─────────────────────────────────────────────────────────────
const DialogHeader = ({ preset, icon, title, subtitle, onClose, children }) => (
  <Box sx={{ background: preset.bg, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 }, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Typography sx={{ fontSize: { xs: 24, sm: 30 }, lineHeight: 1 }}>{icon}</Typography>
      <Box>
        <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: { xs: 15, sm: 17 }, color: "#fff" }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: { xs: 11, sm: 12 }, color: "rgba(255,255,255,0.62)", mt: 0.3 }}>{subtitle}</Typography>}
        {children}
      </Box>
    </Box>
    <IconButton onClick={onClose} size="small"
      sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.22)" }, flexShrink: 0, ml: 1 }}>
      <Close fontSize="small" />
    </IconButton>
  </Box>
);

// ── Mobile card for camps ─────────────────────────────────────────────────────
const CampMobileCard = ({ camp, idx, onEdit, onDelete, onToggle }) => {
  const preset = getPreset(camp.preset_index);
  return (
    <Card variant="outlined" sx={{
      borderRadius: 3, mb: 2, overflow: "hidden",
      bgcolor: camp.is_active ? "#fff" : "#FFF8F8",
      border: `1.5px solid ${camp.is_active ? "#E2E8F0" : "#FECACA"}`,
    }}>
      <Box sx={{ height: 5, background: preset.bg }} />
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: 28 }}>{camp.icon}</Typography>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 14, sm: 16 }, lineHeight: 1.3 }}>{camp.title}</Typography>
              <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: "#64748B", mt: 0.3 }}>{camp.subtitle}</Typography>
              <Typography sx={{ fontSize: 10, color: "#CBD5E1", fontFamily: "monospace", mt: 0.2 }}>{camp.camp_id || camp.id}</Typography>
            </Box>
          </Box>
          <StatusChip active={camp.is_active} />
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
          <Chip label={camp.badge} size="small"
            sx={{ bgcolor: preset.badgeColor, color: "#fff", fontWeight: 800, fontSize: 12 }} />
          <Chip label={`${Array.isArray(camp.tests) ? camp.tests.length : 0} tests`}
            size="small" sx={{ bgcolor: "#E0F7F8", color: "#006D77", fontWeight: 600, fontSize: 12 }} />
          <ColorSwatch presetIndex={camp.preset_index} />
        </Box>

        {Array.isArray(camp.dates) && camp.dates.length > 0 && (
          <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: "#64748B", mb: 1.5 }}>
            📅 {camp.dates.join(", ")}
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1.5, borderTop: "1px solid #F1F5F9" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: "#64748B" }}>
              {camp.is_active ? "Active" : "Inactive"}
            </Typography>
            <Switch size="small" checked={!!camp.is_active} onChange={() => onToggle("camp", camp)} color="success" />
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" startIcon={<Edit sx={{ fontSize: 14 }} />}
              onClick={() => onEdit(camp)}
              sx={{ fontSize: { xs: 12, sm: 13 }, color: "#006D77", bgcolor: "#E0F7F8", "&:hover": { bgcolor: "#B2E0E4" }, borderRadius: 1.5, px: 1.5, textTransform: "none", fontWeight: 700 }}>
              Edit
            </Button>
            <Button size="small" startIcon={<Delete sx={{ fontSize: 14 }} />}
              onClick={() => onDelete("camp", camp)}
              sx={{ fontSize: { xs: 12, sm: 13 }, color: "#DC2626", bgcolor: "#FEE2E2", "&:hover": { bgcolor: "#FECACA" }, borderRadius: 1.5, px: 1.5, textTransform: "none", fontWeight: 700 }}>
              Delete
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// ── Mobile card for packages ──────────────────────────────────────────────────
const PackageMobileCard = ({ pkg, idx, onEdit, onDelete, onToggle }) => {
  const preset = getPreset(pkg.preset_index ?? idx);
  const savings = (pkg.total || 0) - (pkg.offerPrice || 0);
  const discPct = pkg.total > 0 ? Math.round((savings / pkg.total) * 100) : 0;

  return (
    <Card variant="outlined" sx={{
      borderRadius: 3, mb: 2, overflow: "hidden",
      bgcolor: pkg.is_active ? "#fff" : "#FFF8F8",
      border: `1.5px solid ${pkg.is_active ? "#E2E8F0" : "#FECACA"}`,
    }}>
      <Box sx={{ height: 5, background: preset.bg }} />
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: 28 }}>{pkg.icon}</Typography>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 14, sm: 16 }, lineHeight: 1.3 }}>{pkg.name}</Typography>
              <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: "#64748B", mt: 0.3 }}>{pkg.tagline}</Typography>
              <Typography sx={{ fontSize: 10, color: "#CBD5E1", fontFamily: "monospace", mt: 0.2 }}>{pkg.pkg_id || pkg.id}</Typography>
            </Box>
          </Box>
          <StatusChip active={pkg.is_active} />
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5, alignItems: "center" }}>
          <Chip label={pkg.tier || "—"} size="small"
            sx={{ bgcolor: preset.badgeBg, color: preset.textColor, fontWeight: 700, fontSize: 12 }} />
          <Chip label={`${Array.isArray(pkg.tests) ? pkg.tests.length : 0} tests`}
            size="small" sx={{ bgcolor: "#E0F7F8", color: "#006D77", fontWeight: 600, fontSize: 12 }} />
          {discPct > 0 && (
            <Chip label={`${discPct}% OFF`} size="small"
              sx={{ bgcolor: "#ECFDF5", color: "#065F46", fontWeight: 700, fontSize: 12 }} />
          )}
          <ColorSwatch presetIndex={pkg.preset_index ?? idx} />
        </Box>

        <Box sx={{ display: "flex", gap: 3, mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>MRP</Typography>
            <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: "#94A3B8", textDecoration: "line-through" }}>
              ₹{(pkg.total || 0).toLocaleString()}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>OFFER</Typography>
            <Typography sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 800, color: "#059669" }}>
              ₹{(pkg.offerPrice || 0).toLocaleString()}
            </Typography>
          </Box>
          {savings > 0 && (
            <Box>
              <Typography sx={{ fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>SAVINGS</Typography>
              <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 700, color: "#065F46" }}>
                ₹{savings.toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1.5, borderTop: "1px solid #F1F5F9" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: "#64748B" }}>
              {pkg.is_active ? "Active" : "Inactive"}
            </Typography>
            <Switch size="small" checked={!!pkg.is_active} onChange={() => onToggle("package", pkg)} color="success" />
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" startIcon={<Edit sx={{ fontSize: 14 }} />}
              onClick={() => onEdit(pkg)}
              sx={{ fontSize: { xs: 12, sm: 13 }, color: "#006D77", bgcolor: "#E0F7F8", "&:hover": { bgcolor: "#B2E0E4" }, borderRadius: 1.5, px: 1.5, textTransform: "none", fontWeight: 700 }}>
              Edit
            </Button>
            <Button size="small" startIcon={<Delete sx={{ fontSize: 14 }} />}
              onClick={() => onDelete("package", pkg)}
              sx={{ fontSize: { xs: 12, sm: 13 }, color: "#DC2626", bgcolor: "#FEE2E2", "&:hover": { bgcolor: "#FECACA" }, borderRadius: 1.5, px: 1.5, textTransform: "none", fontWeight: 700 }}>
              Delete
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

//  CAMP FORM DIALOG
const CampFormDialog = ({ open, onClose, initial, onSave, saving, communities, }) => {
  const [form, setForm] = useState(emptycamp);

  useEffect(() => {
    setForm(initial ? {
      ...initial,
      preset_index: initial.preset_index ?? 0,
      dates: Array.isArray(initial.dates) ? initial.dates.join(", ") : initial.dates || "",
      tests: Array.isArray(initial.tests) ? initial.tests.join("\n")  : initial.tests || "",
    } : emptycamp);
  }, [initial, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const preset = getPreset(form.preset_index);

  const handleSave = () => {
    const p = getPreset(form.preset_index);
    onSave({
      ...form,
      badgeColor:  p.badgeColor,
      accentColor: p.accentColor,
      bg:          p.bg,
      dates: form.dates ? form.dates.split(",").map((d) => d.trim()).filter(Boolean) : [],
      tests: form.tests ? form.tests.split("\n").map((t) => t.trim()).filter(Boolean) : [],
      sort_order: parseInt(form.sort_order) || 99,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: { xs: 2, sm: 3 }, overflow: "hidden", mx: { xs: 1, sm: 3 } } }}>
      <DialogHeader preset={preset} icon={form.icon || "🏥"}
        title={initial ? "Edit Health Camp" : "Add Health Camp"}
        subtitle={form.title || "New camp"} onClose={onClose} />
      <DialogContent sx={{ pt: 3, pb: 1, px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          <Grid item xs={12} sm={5}>
            <TextField fullWidth label="Camp ID (unique slug)" value={form.id}
              onChange={(e) => set("id", e.target.value)} size="small"
              disabled={!!initial} helperText="e.g. camp1, camp-sugar"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={4} sm={2}>
            <TextField fullWidth label="Icon" value={form.icon}
              onChange={(e) => set("icon", e.target.value)} size="small"
              inputProps={{ style: { fontSize: 22, textAlign: "center" } }} />
          </Grid>
          <Grid item xs={4} sm={3}>
            <TextField fullWidth label="Badge" value={form.badge}
              onChange={(e) => set("badge", e.target.value)} size="small"
              helperText="FREE / ₹99"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={4} sm={2}>
            <TextField fullWidth label="Order" type="number" value={form.sort_order}
              onChange={(e) => set("sort_order", e.target.value)} size="small"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth label="Title *" value={form.title}
              onChange={(e) => set("title", e.target.value)} size="small"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Subtitle" value={form.subtitle}
              onChange={(e) => set("subtitle", e.target.value)} size="small"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={12}>
          <TextField
            select
            fullWidth 
            label="Community*"
            value={form.community || ""}
            onChange={(e) => set("community", e.target.value)}
            size="small"
            required
              sx= {{
                fontSize: { xs: 13, sm: 14 },
                 minWidth: 120 
              }}
          >
            <MenuItem value="">
              Select Community
            </MenuItem>

            {communities.map((community) => (
              <MenuItem
                key={community._id || community.id}
                value={community.name}
              >
                {community.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

          <Grid item xs={12}>
            <PresetPicker value={form.preset_index} onChange={(i) => set("preset_index", i)} />
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth label="Dates (comma separated — e.g. 28-02-2026, 01-03-2026)"
              value={form.dates} onChange={(e) => set("dates", e.target.value)} size="small"
              InputLabelProps={{ sx: { fontSize: { xs: 12, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Tests / Services (one per line)" value={form.tests}
              onChange={(e) => set("tests", e.target.value)} size="small" multiline rows={4}
              helperText="Each line becomes a bullet on the camp card"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Tagline" value={form.tagline}
              onChange={(e) => set("tagline", e.target.value)} size="small"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Note" value={form.note}
              onChange={(e) => set("note", e.target.value)} size="small" multiline rows={2}
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Phone" value={form.phone}
              onChange={(e) => set("phone", e.target.value)} size="small"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Website (optional)" value={form.website}
              onChange={(e) => set("website", e.target.value)} size="small"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={!!form.is_active} onChange={(e) => set("is_active", e.target.checked)} color="success" />}
              label={
                <Typography fontWeight={700} fontSize={{ xs: 13, sm: 14 }} color={form.is_active ? "#065F46" : "#991B1B"}>
                  {form.is_active ? "✅ Active — visible to residents" : "⛔ Inactive — hidden from residents"}
                </Typography>
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1, borderTop: "1px solid #F1F5F9" }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#64748B", fontSize: { xs: 13, sm: 14 } }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ background: preset.bg, textTransform: "none", fontWeight: 700, px: 3, fontSize: { xs: 13, sm: 14 },
            "&:hover": { background: preset.bg, filter: "brightness(0.88)" } }}>
          {saving ? "Saving…" : initial ? "Update Camp" : "Create Camp"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

//  PACKAGE FORM DIALOG
const PackageFormDialog = ({ open, onClose, initial, onSave, saving }) => {
  const [form, setForm] = useState(emptypkg);

  useEffect(() => {
    setForm(initial ? {
      ...initial,
      preset_index: initial.preset_index ?? 0,
      tests: Array.isArray(initial.tests)
        ? initial.tests.map((t) => `${t.code || "—"}|${t.name}|${t.mrp || 0}`).join("\n")
        : initial.tests || "",
    } : emptypkg);
  }, [initial, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const preset  = getPreset(form.preset_index);
  const savings = parseFloat(form.total || 0) - parseFloat(form.offerPrice || 0);
  const discPct = form.total > 0 ? Math.round((savings / form.total) * 100) : 0;

  const handleSave = () => {
    const p = getPreset(form.preset_index);
    const testLines = form.tests
      ? form.tests.split("\n").map((line) => {
          const [code, name, mrp] = line.split("|").map((s) => s.trim());
          return { code: code || "—", name: name || code || "", mrp: parseFloat(mrp) || 0 };
        }).filter((t) => t.name)
      : [];
    onSave({
      ...form,
      badgeColor:  p.textColor,
      badgeBg:     p.badgeBg,
      accentColor: p.accentColor,
      gradient:    p.bg,
      tests:       testLines,
      total:       parseFloat(form.total)      || 0,
      offerPrice:  parseFloat(form.offerPrice) || 0,
      sort_order:  parseInt(form.sort_order)   || 99,
      test_count:  parseFloat(form.test_count),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: { xs: 2, sm: 3 }, overflow: "hidden", mx: { xs: 1, sm: 3 } } }}>
      <DialogHeader preset={preset} icon={form.icon || "💊"}
        title={initial ? "Edit Health Package" : "Add Health Package"}
        subtitle={form.name || "New package"} onClose={onClose}>
        {form.offerPrice && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <Typography sx={{ fontFamily: "'Sora',sans-serif", fontSize: { xs: 15, sm: 18 }, fontWeight: 900, color: preset.accentColor }}>
              ₹{parseFloat(form.offerPrice).toLocaleString()}
            </Typography>
            {discPct > 0 && (
              <Chip label={`${discPct}% OFF`} size="small"
                sx={{ bgcolor: "rgba(16,185,129,0.25)", color: "#80ED99", fontWeight: 800, fontSize: 11, height: 20 }} />
            )}
          </Box>
        )}
      </DialogHeader>

      <DialogContent sx={{ pt: 3, pb: 1, px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          <Grid item xs={12} sm={5}>
            <TextField fullWidth label="Package ID (unique slug)" value={form.id}
              onChange={(e) => set("id", e.target.value)} size="small"
              disabled={!!initial} helperText="e.g. pkg-silver"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={4} sm={2}>
            <TextField fullWidth label="Icon" value={form.icon}
              onChange={(e) => set("icon", e.target.value)} size="small"
              inputProps={{ style: { fontSize: 22, textAlign: "center" } }} />
          </Grid>
          <Grid item xs={4} sm={3}>
            <TextField fullWidth label="Tier" value={form.tier}
              onChange={(e) => set("tier", e.target.value)} size="small"
              helperText="Silver, Gold, Elite…"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={4} sm={2}>
            <TextField fullWidth label="Order" type="number" value={form.sort_order}
              onChange={(e) => set("sort_order", e.target.value)} size="small"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth label="Package Name *" value={form.name}
              onChange={(e) => set("name", e.target.value)} size="small"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Tagline" value={form.tagline}
              onChange={(e) => set("tagline", e.target.value)} size="small"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>

          <Grid item xs={6}>
            <TextField fullWidth label="Total MRP (₹) *" type="number" value={form.total}
              onChange={(e) => set("total", e.target.value)} size="small"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Offer Price (₹) *" type="number" value={form.offerPrice}
              onChange={(e) => set("offerPrice", e.target.value)} size="small"
              InputLabelProps={{ sx: { fontSize: { xs: 13, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(13px, 2vw, 15px)" } }} />
          </Grid>

          <Grid item xs={12}>
            <PresetPicker value={form.preset_index} onChange={(i) => set("preset_index", i)} />
          </Grid>

          <Grid item xs={12}>
            
              <TextField
              sx={{mb:3 }}
                fullWidth
                label="Number of Tests"
                type="number"
                value={form.test_count}
                onChange={(e) => {
                  set("test_count",e.target.value);
                }}
                size="small"
              />
            <TextField fullWidth label="Tests (one per line — CODE|Test Name|MRP)"
              value={form.tests} onChange={(e) => set("tests", e.target.value)}
              size="small" multiline rows={8}
              helperText="Format: PR0305|CBC with ESR|500  — code and MRP are optional"
              InputLabelProps={{ sx: { fontSize: { xs: 12, sm: 14 } } }}
              inputProps={{ style: { fontSize: "clamp(12px, 1.8vw, 14px)" } }} />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={!!form.is_active} onChange={(e) => set("is_active", e.target.checked)} color="success" />}
              label={
                <Typography fontWeight={700} fontSize={{ xs: 13, sm: 14 }} color={form.is_active ? "#065F46" : "#991B1B"}>
                  {form.is_active ? "✅ Active — visible to residents" : "⛔ Inactive — hidden from residents"}
                </Typography>
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1, borderTop: "1px solid #F1F5F9" }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#64748B", fontSize: { xs: 13, sm: 14 } }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ background: preset.bg, textTransform: "none", fontWeight: 700, px: 3, fontSize: { xs: 13, sm: 14 },
            "&:hover": { background: preset.bg, filter: "brightness(0.88)" } }}>
          {saving ? "Saving…" : initial ? "Update Package" : "Create Package"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

//  MAIN PAGE
export default function AdminHealthContent() {
  const theme = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down("sm"));   // < 600px
  const isTablet  = useMediaQuery(theme.breakpoints.down("md"));   // < 900px

  const [tab,           setTab]          = useState(0);
  const [camps,         setCamps]        = useState([]);
  const [packages,      setPackages]     = useState([]);
  const [loading,       setLoading]      = useState(true);
  const [saving,        setSaving]       = useState(false);
  const [campDialog,    setCampDialog]   = useState(false);
  const [pkgDialog,     setPkgDialog]    = useState(false);
  const [editCamp,      setEditCamp]     = useState(null);
  const [editPkg,       setEditPkg]      = useState(null);
  const [deleteConfirm, setDeleteConfirm]= useState(null);
  const [snack,         setSnack]        = useState({ open: false, msg: "", severity: "success" });
  const [communities, setCommunities] = useState([]);
  const notify = (msg, severity = "success") => setSnack({ open: true, msg, severity });
    const [snackbar, setSnackbar] = useState({
      open: false,
      message: "",
      severity: "success",
    });
const API_URL = process.env.REACT_APP_API

useEffect(() => {
    fetchCommunities()
   },[])
  const fetchCommunities = async () => {
  
      try {
  
        const response = await axios.get(
          `${API_URL}/community/config`
        );
  
        setCommunities(
          response.data.communities || []
        );
  
      } catch (error) {
  
        console.error(
          "Error fetching communities:",
          error
        );
  
        showSnackbar(
          "Failed to load communities",
          "error"
        );
  
      } 
    };
  
const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };


  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cr, pr] = await Promise.all([
        apiRequest(`${API}/camps`),
        apiRequest(`${API}/packages`),
      ]);
      const cd = await cr.json();
      const pd = await pr.json();
      if (cd.success) setCamps(cd.camps       || []);
      if (pd.success) setPackages(pd.packages || []);
    } catch {
      notify("Failed to fetch data", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleActive = async (type, item) => {
    try {
      const res = await apiRequest(
        type === "camp" ? `${API}/camps/${item.id}` : `${API}/packages/${item.id}`,
        { method: "PATCH", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_active: !item.is_active }) }
      );
      if (res.ok) {
        notify(`${type === "camp" ? "Camp" : "Package"} ${!item.is_active ? "activated" : "deactivated"}`);
        fetchAll();
      } else notify("Update failed", "error");
    } catch { notify("Update failed", "error"); }
  };

  const saveCamp = async (payload) => {
    setSaving(true);
    try {
      const isEdit = !!editCamp;
      const res = await apiRequest(
        isEdit ? `${API}/camps/${editCamp.id}` : `${API}/camps`,
        { method: isEdit ? "PATCH" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        notify(isEdit ? "Camp updated!" : "Camp created!");
        setCampDialog(false); setEditCamp(null); fetchAll();
      } else notify(data.error || "Save failed", "error");
    } catch { notify("Save failed", "error"); }
    finally { setSaving(false); }
  };

  const savePackage = async (payload) => {
    setSaving(true);
    try {
      const isEdit = !!editPkg;
      const res = await apiRequest(
        isEdit ? `${API}/packages/${editPkg.id}` : `${API}/packages`,
        { method: isEdit ? "PATCH" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        notify(isEdit ? "Package updated!" : "Package created!");
        setPkgDialog(false); setEditPkg(null); fetchAll();
      } else notify(data.error || "Save failed", "error");
    } catch { notify("Save failed", "error"); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const { type, item } = deleteConfirm;
    try {
      const res = await apiRequest(
        type === "camp" ? `${API}/camps/${item.id}` : `${API}/packages/${item.id}`,
        { method: "DELETE" }
      );
      if (res.ok) { notify(`${type === "camp" ? "Camp" : "Package"} deleted`); fetchAll(); }
      else notify("Delete failed", "error");
    } catch { notify("Delete failed", "error"); }
    finally { setDeleteConfirm(null); }
  };

  const activeCamps    = camps.filter((c) => c.is_active).length;
  const activePackages = packages.filter((p) => p.is_active).length;

  if (loading) return (
    <Box sx={{
      ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
      display: "flex", justifyContent: "center", alignItems: "center",
      minHeight: "80vh", flexDirection: "column", gap: 2,
    }}>
      <CircularProgress sx={{ color: "#006D77" }} />
      <Typography sx={{ color: "#64748B", fontSize: { xs: 14, sm: 15 } }}>Loading health content…</Typography>
    </Box>
  );

  // ── Table header cell style
  const TH = {
    fontWeight: 700,
    fontSize: { xs: 11, sm: 12, md: 13 },
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    py: { xs: 1, sm: 1.5 },
    whiteSpace: "nowrap",
  };

  // ── Table body cell style
  const TD = {
    fontSize: { xs: 12, sm: 13, md: 14 },
    py: { xs: 1, sm: 1.2 },
  };

  return (
    // ── KEY: offset left margin on desktop to clear the sidebar ──────────────
    <Box sx={{
      ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
      minHeight: "100vh",
      bgcolor: "#F1F5F9",
      pt: { xs: "56px", md: 0 }, 
    }}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1.5, sm: 3 } }}>
        <Paper elevation={0} sx={{ borderRadius: { xs: 2, sm: 3 }, overflow: "hidden", border: "1px solid #E2E8F0" }}>

          {/* ── Header ── */}
          <Box sx={{
            background: "linear-gradient(135deg, #003D45 0%, #006D77 55%, #008B98 100%)",
            px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2, sm: 2.5, md: 3 },
            display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2,
          }}>
            <Box>
              <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: { xs: 17, sm: 19, md: 21 }, color: "#fff" }}>
                Health Content Manager
              </Typography>
              <Typography sx={{ fontSize: { xs: 11, sm: 12, md: 13 }, color: "rgba(255,255,255,0.6)", mt: 0.4 }}>
                Toggle active/inactive to control what residents see · {COLOR_PRESETS.length} colour themes available
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip label={`${activeCamps}/${camps.length} camps`}
                sx={{ bgcolor: "rgba(128,237,153,0.15)", color: "#80ED99", fontWeight: 700, fontSize: { xs: 11, sm: 12 }, border: "1px solid rgba(128,237,153,0.3)" }} />
              <Chip label={`${activePackages}/${packages.length} pkgs`}
                sx={{ bgcolor: "rgba(128,237,153,0.15)", color: "#80ED99", fontWeight: 700, fontSize: { xs: 11, sm: 12 }, border: "1px solid rgba(128,237,153,0.3)" }} />
            </Box>
          </Box>

          {/* ── Colour preset legend ── */}
          {/* <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 1.5, bgcolor: "#F8FAFC", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 }, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Themes:
            </Typography>
            {COLOR_PRESETS.map((p, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                <Box sx={{ width: { xs: 12, sm: 14 }, height: { xs: 12, sm: 14 }, borderRadius: 0.5, background: p.bg, border: "1px solid rgba(0,0,0,0.1)" }} />
                <Typography sx={{ fontSize: { xs: 11, sm: 12 }, color: "#64748B" }}>{p.label}</Typography>
              </Box>
            ))}
          </Box> */}

          {/* ── Tabs ── */}
          <Tabs value={tab} onChange={(_, v) => setTab(v)}
            variant={isMobile ? "fullWidth" : "standard"}
            sx={{ px: { xs: 0, sm: 3 }, bgcolor: "#FAFAFA", borderBottom: "1px solid #E2E8F0" }}
            TabIndicatorProps={{ sx: { bgcolor: "#006D77", height: 3, borderRadius: "3px 3px 0 0" } }}>
            <Tab label={`🏕️ Camps (${camps.length})`}
              sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, textTransform: "none", fontSize: { xs: 13, sm: 14, md: 15 } }} />
            <Tab label={`💊 Packages (${packages.length})`}
              sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, textTransform: "none", fontSize: { xs: 13, sm: 14, md: 15 } }} />
          </Tabs>

          {/* ══ CAMPS TAB ══ */}
          {tab === 0 && (
            <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: 2, sm: 2.5 } }}>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: 15, sm: 17, md: 18 } }}>Health Camps</Typography>
                  <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: "#64748B" }}>
                    {activeCamps} active · {camps.length - activeCamps} inactive
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />}
                  onClick={() => { setEditCamp(null); setCampDialog(true); }}
                  sx={{ bgcolor: "#006D77", textTransform: "none", fontWeight: 700, borderRadius: 2, fontSize: { xs: 13, sm: 14 }, "&:hover": { bgcolor: "#005560" } }}>
                  {isMobile ? "Add" : "Add Camp"}
                </Button>
              </Box>

              {/* Mobile / Tablet → cards; Desktop → table */}
              {isTablet ? (
                <Box>
                  {camps.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 6 }}>
                      <Typography sx={{ color: "#94A3B8", fontSize: 15 }}>No camps yet</Typography>
                      <Typography sx={{ color: "#CBD5E1", fontSize: 13, mt: 0.5 }}>Click "Add Camp" to create the first one</Typography>
                    </Box>
                  ) : camps.map((camp, idx) => (
                    <CampMobileCard key={camp.id ?? idx} camp={camp} idx={idx}
                      onEdit={(c) => { setEditCamp(c); setCampDialog(true); }}
                      onDelete={(type, item) => setDeleteConfirm({ type, item })}
                      onToggle={toggleActive} />
                  ))}
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 700 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                        {["#", "Camp", "Badge", "Theme", "Tests","COMMUNITY", "Dates", "Status", "Actions"].map((h) => (
                          <TableCell key={h} sx={{ ...TH }} align={["Actions", "Status"].includes(h) ? "center" : "left"}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {camps.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                            <Typography sx={{ color: "#94A3B8", fontSize: 15 }}>No camps yet</Typography>
                            <Typography sx={{ color: "#CBD5E1", fontSize: 13, mt: 0.5 }}>Click "Add Camp" to create the first one</Typography>
                          </TableCell>
                        </TableRow>
                      ) : camps.map((camp, idx) => (
                        <TableRow key={camp.id ?? idx} sx={{
                          bgcolor: camp.is_active ? "#fff" : "#FFF8F8",
                          "&:hover": { bgcolor: camp.is_active ? "#F0FDFA" : "#FEF2F2" },
                          transition: "background 0.15s",
                        }}>
                          <TableCell sx={{ ...TD, color: "#CBD5E1", width: 36 }}>{idx + 1}</TableCell>
                          <TableCell sx={{ ...TD, maxWidth: 220 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography sx={{ fontSize: { sm: 20, md: 22 }, lineHeight: 1, flexShrink: 0 }}>{camp.icon}</Typography>
                              <Box sx={{ minWidth: 0 }}>   {/* minWidth:0 is required for ellipsis to work inside flex */}
                                <Typography sx={{ fontWeight: 700, fontSize: { sm: 13, md: 14 }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{camp.title}</Typography>
                                <Typography sx={{ fontSize: { sm: 11, md: 12 }, color: "#94A3B8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{camp.subtitle}</Typography>
                                <Typography sx={{ fontSize: 10, color: "#CBD5E1", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{camp.camp_id || camp.id}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...TD }}>
                            <Chip label={camp.badge} size="small"
                              sx={{ bgcolor: getPreset(camp.preset_index).badgeColor, color: "#fff", fontWeight: 800, fontSize: { sm: 11, md: 12 } }} />
                          </TableCell>
                          <TableCell sx={{ ...TD }}><ColorSwatch presetIndex={camp.preset_index} /></TableCell>
                          <TableCell sx={{ ...TD }}>
                            <Chip label={`${Array.isArray(camp.tests) ? camp.tests.length : 0} tests`}
                              size="small" sx={{ bgcolor: "#E0F7F8", color: "#006D77", fontWeight: 600, fontSize: { sm: 11, md: 12 } }} />
                          </TableCell>
                          <TableCell><Typography>{camp.community }</Typography></TableCell>
                          <TableCell sx={{ ...TD, color: "#64748B", maxWidth: 150 }}>
                            {Array.isArray(camp.dates) && camp.dates.length > 0 ? camp.dates.join(", ") : "—"}
                          </TableCell>
                          <TableCell align="center" sx={{ ...TD }}>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                              <StatusChip active={camp.is_active} />
                              <Tooltip title={camp.is_active ? "Deactivate" : "Activate"}>
                                <Switch size="small" checked={!!camp.is_active} onChange={() => toggleActive("camp", camp)} color="success" />
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ ...TD }}>
                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => { setEditCamp(camp); setCampDialog(true); }}
                                  sx={{ color: "#006D77", bgcolor: "#E0F7F8", "&:hover": { bgcolor: "#B2E0E4" }, width: 30, height: 30 }}>
                                  <Edit sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => setDeleteConfirm({ type: "camp", item: camp })}
                                  sx={{ color: "#DC2626", bgcolor: "#FEE2E2", "&:hover": { bgcolor: "#FECACA" }, width: 30, height: 30 }}>
                                  <Delete sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* ══ PACKAGES TAB ══ */}
          {tab === 1 && (
            <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: 2, sm: 2.5 } }}>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: 15, sm: 17, md: 18 } }}>Health Packages</Typography>
                  <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: "#64748B" }}>
                    {activePackages} active · {packages.length - activePackages} inactive
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />}
                  onClick={() => { setEditPkg(null); setPkgDialog(true); }}
                  sx={{ bgcolor: "#006D77", textTransform: "none", fontWeight: 700, borderRadius: 2, fontSize: { xs: 13, sm: 14 }, "&:hover": { bgcolor: "#005560" } }}>
                  {isMobile ? "Add" : "Add Package"}
                </Button>
              </Box>

              {/* Mobile / Tablet → cards; Desktop → table */}
              {isTablet ? (
                <Box>
                  {packages.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 6 }}>
                      <Typography sx={{ color: "#94A3B8", fontSize: 15 }}>No packages yet</Typography>
                      <Typography sx={{ color: "#CBD5E1", fontSize: 13, mt: 0.5 }}>Click "Add Package" to create the first one</Typography>
                    </Box>
                  ) : packages.map((pkg, idx) => (
                    <PackageMobileCard key={pkg.id ?? idx} pkg={pkg} idx={idx}
                      onEdit={(p) => { setEditPkg(p); setPkgDialog(true); }}
                      onDelete={(type, item) => setDeleteConfirm({ type, item })}
                      onToggle={toggleActive} />
                  ))}
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                        {["#", "Package", "Tier", "Theme", "MRP", "Offer", "Savings", "Tests", "Status", "Actions"].map((h) => (
                          <TableCell key={h} sx={{ ...TH }}
                            align={["MRP", "Offer", "Savings", "Actions", "Status"].includes(h) ? "center" : "left"}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {packages.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                            <Typography sx={{ color: "#94A3B8", fontSize: 15 }}>No packages yet</Typography>
                            <Typography sx={{ color: "#CBD5E1", fontSize: 13, mt: 0.5 }}>Click "Add Package" to create the first one</Typography>
                          </TableCell>
                        </TableRow>
                      ) : packages.map((pkg, idx) => {
                        const savings = (pkg.total || 0) - (pkg.offerPrice || 0);
                        const discPct = pkg.total > 0 ? Math.round((savings / pkg.total) * 100) : 0;
                        const preset  = getPreset(pkg.preset_index ?? idx);
                        return (
                          <TableRow key={pkg.id ?? idx} sx={{
                            bgcolor: pkg.is_active ? "#fff" : "#FFF8F8",
                            "&:hover": { bgcolor: pkg.is_active ? "#F0FDFA" : "#FEF2F2" },
                            transition: "background 0.15s",
                          }}>
                            <TableCell sx={{ ...TD, color: "#CBD5E1", width: 36 }}>{idx + 1}</TableCell>
                            <TableCell sx={{ ...TD, maxWidth: 220 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography sx={{ fontSize: { sm: 20, md: 22 }, lineHeight: 1, flexShrink: 0 }}>{pkg.icon}</Typography>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 700, fontSize: { sm: 13, md: 14 }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pkg.name}</Typography>
                                  <Typography sx={{ fontSize: { sm: 11, md: 12 }, color: "#94A3B8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pkg.tagline}</Typography>
                                  <Typography sx={{ fontSize: 10, color: "#CBD5E1", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pkg.pkg_id || pkg.id}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ ...TD }}>
                              <Chip label={pkg.tier || "—"} size="small"
                                sx={{ bgcolor: preset.badgeBg, color: preset.textColor, fontWeight: 700, fontSize: { sm: 11, md: 12 } }} />
                            </TableCell>
                            <TableCell sx={{ ...TD }}><ColorSwatch presetIndex={pkg.preset_index ?? idx} /></TableCell>
                            <TableCell align="center" sx={{ ...TD }}>
                              <Typography sx={{ fontSize: { sm: 12, md: 13 }, color: "#94A3B8", textDecoration: "line-through" }}>
                                ₹{(pkg.total || 0).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ ...TD }}>
                              <Typography sx={{ fontSize: { sm: 13, md: 14 }, fontWeight: 700, color: "#059669" }}>
                                ₹{(pkg.offerPrice || 0).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ ...TD }}>
                              {discPct > 0
                                ? <Chip label={`${discPct}% OFF`} size="small"
                                    sx={{ bgcolor: "#ECFDF5", color: "#065F46", fontWeight: 700, fontSize: { sm: 11, md: 12 } }} />
                                : "—"}
                            </TableCell>
                            <TableCell sx={{ ...TD }}>
                              <Chip label={`${Array.isArray(pkg.tests) ? pkg.tests.length : 0} tests`}
                                size="small" sx={{ bgcolor: "#E0F7F8", color: "#006D77", fontWeight: 600, fontSize: { sm: 11, md: 12 } }} />
                            </TableCell>
                            <TableCell align="center" sx={{ ...TD }}>
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                                <StatusChip active={pkg.is_active} />
                                <Tooltip title={pkg.is_active ? "Deactivate" : "Activate"}>
                                  <Switch size="small" checked={!!pkg.is_active} onChange={() => toggleActive("package", pkg)} color="success" />
                                </Tooltip>
                              </Box>
                            </TableCell>
                            <TableCell align="center" sx={{ ...TD }}>
                              <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => { setEditPkg(pkg); setPkgDialog(true); }}
                                    sx={{ color: "#006D77", bgcolor: "#E0F7F8", "&:hover": { bgcolor: "#B2E0E4" }, width: 30, height: 30 }}>
                                    <Edit sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton size="small" onClick={() => setDeleteConfirm({ type: "package", item: pkg })}
                                    sx={{ color: "#DC2626", bgcolor: "#FEE2E2", "&:hover": { bgcolor: "#FECACA" }, width: 30, height: 30 }}>
                                    <Delete sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Paper>
      </Container>

      {/* ── Dialogs ── */}
      <CampFormDialog open={campDialog} onClose={() => { setCampDialog(false); setEditCamp(null); }}
        initial={editCamp} onSave={saveCamp} saving={saving}   communities={communities} />
      <PackageFormDialog open={pkgDialog} onClose={() => { setPkgDialog(false); setEditPkg(null); }}
        initial={editPkg} onSave={savePackage} saving={saving} />

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}
        PaperProps={{ sx: { borderRadius: 3, mx: { xs: 2, sm: "auto" } } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: { xs: 15, sm: 17 } }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: 2, fontSize: { xs: 13, sm: 14 } }}>
            Permanently delete <strong>"{deleteConfirm?.item?.title || deleteConfirm?.item?.name}"</strong>?{" "}
            This cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteConfirm(null)}
            sx={{ textTransform: "none", fontSize: { xs: 13, sm: 14 } }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}
            sx={{ textTransform: "none", fontWeight: 700, fontSize: { xs: 13, sm: 14 } }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ fontWeight: 600, borderRadius: 2, fontSize: { xs: 13, sm: 14 } }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}